import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/seo/Seo';
import { breadcrumbSchema } from '../components/seo/jsonld';

const DisclaimerPage = () => (
    <>
        <Seo
            title="Disclaimer"
            description="Disclaimer for pdfconvertor.shop — conversion accuracy, third-party links, advertising, and use-at-your-own-risk statement."
            path="/disclaimer"
            jsonLd={breadcrumbSchema([
                { name: 'Home', path: '/' },
                { name: 'Disclaimer', path: '/disclaimer' },
            ])}
        />

        <div className="page-container narrow">
            <article className="legal-page">
                <header className="page-header">
                    <h1>Disclaimer</h1>
                    <p className="meta">Last updated: April 21, 2026</p>
                </header>

                <p>
                    The information and tools provided on pdfconvertor.shop (the &quot;Site&quot;)
                    are for general informational and utility purposes only. Please read this
                    disclaimer carefully before using the Site.
                </p>

                <h2>1. Use at your own risk</h2>
                <p>
                    The tools on this Site process documents locally in your browser. While we
                    have built them to be reliable, we cannot guarantee the accuracy,
                    completeness, or fitness of conversion output for any particular purpose.
                    You use the Site at your own risk.
                </p>
                <p>
                    Always keep a backup of the original file before converting. If a conversion
                    loses formatting, content, or fidelity, we cannot recover the original — we
                    never received it.
                </p>

                <h2>2. Not professional advice</h2>
                <p>
                    Nothing on this Site constitutes legal, financial, medical, or other
                    professional advice. Articles and guides are intended as general education,
                    not prescription. If you need professional advice — especially about
                    document authenticity, compliance, or legal signatures — consult a qualified
                    professional.
                </p>

                <h2>3. Conversion accuracy</h2>
                <p>
                    Document conversion is inherently lossy across many formats. In particular:
                </p>
                <ul>
                    <li>
                        <strong>Word-to-PDF</strong> may not perfectly preserve complex layouts,
                        tables with merged cells, headers, footers, or embedded objects.
                    </li>
                    <li>
                        <strong>PDF-to-Word</strong> extracts text only; images, real table
                        structure, columns, and precise styling are not reconstructed.
                    </li>
                    <li>
                        <strong>PDF compression</strong> here is lossless; sizeable savings
                        typically come from PDFs with redundant internal structure, not from
                        image recompression.
                    </li>
                </ul>
                <p>
                    For high-stakes documents (legal contracts, certified submissions, regulated
                    filings), verify the output manually and consider using officially licensed
                    desktop software.
                </p>

                <h2>4. External links</h2>
                <p>
                    The Site contains links to third-party websites. We have no control over the
                    content or practices of these websites and are not responsible for them.
                    Following external links is at your own discretion.
                </p>

                <h2>5. Advertising disclosure</h2>
                <p>
                    This Site displays advertising served by{' '}
                    <strong>Google AdSense</strong> and its advertising partners. We earn
                    revenue when ads are displayed and, in some cases, when users interact with
                    them. The presence of an advertisement does not imply our endorsement of the
                    advertised product or service.
                </p>
                <p>
                    We do not control which ads are shown — Google selects ads based on user
                    interests, site content, and its own policies. If you see an ad that appears
                    misleading or inappropriate, please report it via the{' '}
                    <Link to="/contact">contact form</Link> and also through Google's ad
                    feedback tools.
                </p>

                <h2>6. No warranty of availability</h2>
                <p>
                    We do our best to keep the Site online and functional, but we do not
                    guarantee uninterrupted availability. The Site may be unavailable at times
                    due to maintenance, infrastructure issues, or circumstances beyond our
                    control.
                </p>

                <h2>7. Changes to this disclaimer</h2>
                <p>
                    We may revise this disclaimer from time to time. The latest version is
                    always posted on this page, with an updated &quot;Last updated&quot; date.
                    Continued use of the Site after changes constitutes acceptance.
                </p>

                <h2>8. Contact</h2>
                <p>
                    Questions? Reach us at{' '}
                    <a href="mailto:pdf2convertor@gmail.com">pdf2convertor@gmail.com</a> or via
                    the <Link to="/contact">contact form</Link>.
                </p>
            </article>
        </div>
    </>
);

export default DisclaimerPage;
